import { CourseMaterial, useCoursesLoaderData } from "./coursesLoader.ts";
import { useLayoutData } from "../layout/myCoursesLayoutLoader.ts";
import {
  getStoreProducts,
  isMaterialInStoreCart,
} from "./common/storeProductUtils.ts";

export default function useTermStoreMetrics() {
  const { courses } = useCoursesLoaderData();
  const layoutData = useLayoutData();
  const { currentTerm } = useCoursesLoaderData();
  const isEAOptedOut =
    currentTerm?.programDecision === "no_program" &&
    currentTerm.institutionProgram === "equitable_access";

  // Only calculate metrics for courses where the user is a learner
  const getLearnerMaterials = courses.reduce(
    (acc, { userRole, courseMaterials }) => {
      if (userRole === "learner" && courseMaterials) {
        acc.push(...courseMaterials);
      }
      return acc;
    },
    [] as CourseMaterial[]
  );

  // note in EA opted out, in program materials are considered included
  // but flagged excluded in an attempt to lure the user back into the program
  const requiredToPurchaseBase = getLearnerMaterials.filter(
    (material) =>
      material.requiredCode === "required" &&
      (material.materialStatus === "not_included" ||
        material.materialStatus === "opted_out")
  );

  // note in EA opted out, in program materials are considered included
  // but flagged excluded in an attempt to lure the user back into the program
  const requiredToPurchaseActual = requiredToPurchaseBase.filter(
    (material) => !(material.materialStatus === "opted_out" && isEAOptedOut)
  );

  // if no product then they can't purchase it, if its in the cart then we don't need to show it
  // todo already have a license
  const requiredToPurchase = requiredToPurchaseActual.filter(
    (material) =>
      material.product &&
      !isMaterialInStoreCart(material, layoutData.storeCart?.lineItems)
  );

  const requiredIncluded = getLearnerMaterials.filter(
    (material) =>
      material.requiredCode === "required" &&
      (material.materialStatus === "opted_in" ||
        (isEAOptedOut && material.materialStatus === "opted_out"))
  );

  const optableItems = courses
    .map((course) =>
      (course.courseMaterials ?? []).filter(
        (material) =>
          material.materialStatus === "opted_in" ||
          material.materialStatus === "opted_out"
      )
    )
    .flat();

  const requiredToPurchaseTotalCost = requiredToPurchase.reduce((acc, cur) => {
    const { defaultVariant } = getStoreProducts(cur);
    const priceStr = defaultVariant?.studentPrice;
    if (priceStr) {
      const price = parseFloat(priceStr);
      if (!isNaN(price)) {
        return acc + price;
      }
    }
    return acc;
  }, 0);

  const requiredToPurchaseInCart = requiredToPurchaseBase.filter((material) =>
    isMaterialInStoreCart(material, layoutData.storeCart?.lineItems)
  );

  const withPricing = requiredToPurchase.find(
    (material) => material.product?.[0]?.variants
  );

  const programCost = requiredIncluded.reduce<number>((acc, cur) => {
    const price = Number(cur.studentPrice);
    if (isNaN(price)) {
      return acc;
    }
    return (acc += price);
  }, 0);

  const requiredToPurchaseDefaultVariantIds = requiredToPurchase
    .map((material) => {
      const { defaultVariant } = getStoreProducts(material);
      return defaultVariant?.id;
    })
    .filter<number>((id): id is number => id !== undefined);

  return {
    numCourses: courses.length,
    requiredToPurchase,
    requiredToPurchaseCount: requiredToPurchase.length,
    requiredToPurchaseInCartCount: requiredToPurchaseInCart.length,
    requiredToPurchaseDefaultVariantIds,
    requiredToPurchaseBaseCount: requiredToPurchaseBase.length,
    needsPurchase: !!(
      requiredToPurchaseInCart.length !== requiredToPurchase.length &&
      requiredToPurchase.length
    ),
    requiredToPurchaseTotalCost,
    purchaseCurrency:
      withPricing?.product?.[0].variants[0].studentCurrency ?? "USD",
    requiredIncludedCount: requiredIncluded.length,
    totalRequiredMaterials: requiredToPurchase.length + requiredIncluded.length,
    alreadyPurchasedCount: 0, // todo add this when available
    programCost,
    hasOptableItems: optableItems.length > 0,
  };
}
